import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, useTheme, Alert, CircularProgress } from '@mui/material';
import { fetchDevicePictures, fetchDebugCountStatus, requestVisualConfirmation } from '../services/apiService';
import { WalteroCard, WalteroTypography, WalteroButton } from './waltero';
import ImageModal from './shared/ImageModal';
import './VisualReview.css';

const VisualReview = ({ reportData, selectedOrgId, startDate, endDate }) => {
  const theme = useTheme();
  const [pictureCache, setPictureCache] = useState({});
  const [loadingDevices, setLoadingDevices] = useState(new Set());
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalPictures, setModalPictures] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [debugCountStatus, setDebugCountStatus] = useState({});
  const [loadingDebugCount, setLoadingDebugCount] = useState(false);
  const [requestingConfirmation, setRequestingConfirmation] = useState(false);
  const [requestResult, setRequestResult] = useState(null);
  const [showRequestResult, setShowRequestResult] = useState(false);

  // Generate date range from start and end dates
  const dates = useMemo(() => {
    if (!startDate || !endDate) return [];
    
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]); // YYYY-MM-DD format
    }
    
    return dates.sort().reverse(); // Most recent first
  }, [startDate, endDate]);

  // Load pictures for a device
  const loadDevicePictures = useCallback(async (deviceId) => {
    if (pictureCache[deviceId] || loadingDevices.has(deviceId)) {
      return; // Already loaded or loading
    }

    setLoadingDevices(prev => new Set([...prev, deviceId]));

    try {
      console.log(`📸 Loading pictures for device: ${deviceId} (${startDate} to ${endDate})`);
      const picturesData = await fetchDevicePictures(deviceId, {
        startDate,
        endDate,
        limit: 500 // Reasonable limit for visual review
      });
      const pictures = picturesData.pictures || [];
      
      // Group pictures by date
      const picturesByDate = {};
      pictures.forEach(picture => {
        const pictureDate = extractDateFromPicture(picture);
        if (pictureDate) {
          if (!picturesByDate[pictureDate]) {
            picturesByDate[pictureDate] = [];
          }
          picturesByDate[pictureDate].push(picture);
        }
      });

      // Sort pictures within each date (prioritize inference images, then newest first)
      Object.keys(picturesByDate).forEach(date => {
        picturesByDate[date].sort((a, b) => {
          const aIsInference = a.folder === 'inference';
          const bIsInference = b.folder === 'inference';
          
          // Prioritize inference images first
          if (aIsInference && !bIsInference) return -1;
          if (!aIsInference && bIsInference) return 1;
          
          // If both are same type (both inference or both root), sort by newest first
          return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
        });
      });

      setPictureCache(prev => ({
        ...prev,
        [deviceId]: picturesByDate
      }));
      
      console.log(`✅ Loaded ${pictures.length} pictures for device ${deviceId}, grouped by ${Object.keys(picturesByDate).length} dates`);
    } catch (error) {
      console.error(`❌ Error loading pictures for device ${deviceId}:`, error);
      setPictureCache(prev => ({
        ...prev,
        [deviceId]: {} // Empty object indicates we tried but failed
      }));
    } finally {
      setLoadingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
    }
  }, [pictureCache, loadingDevices, startDate, endDate]);

  // Utility function to check if an image is from inference folder
  const isInferenceImage = (picture) => {
    const isInference = picture.folder === 'inference';
    if (isInference) {
      console.log(`🔍 Detected inference image: ${picture.filename} (folder: ${picture.folder})`);
    }
    return isInference;
  };

  // Check if any images for a date contain inference images
  const hasInferenceImagesForDate = (deviceId, date) => {
    const pictures = getPicturesForDeviceAndDate(deviceId, date);
    const hasInference = pictures.some(picture => picture.folder === 'inference');
    if (hasInference) {
      console.log(`🔵 Date ${date} for device ${deviceId} contains inference images`);
    }
    return hasInference;
  };

  // Extract date from picture filename or lastModified
  const extractDateFromPicture = (picture) => {
    // Try to extract date from filename first (format like 20241207T235330Z.jpeg)
    const match = picture.filename.match(/(\d{4})(\d{2})(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Fallback to lastModified date
    if (picture.lastModified) {
      const date = new Date(picture.lastModified);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    }
    
    return null;
  };

  // Get pictures for a device and date
  const getPicturesForDeviceAndDate = (deviceId, date) => {
    const devicePictures = pictureCache[deviceId];
    if (!devicePictures) return [];
    return devicePictures[date] || [];
  };

  // Handle image click
  const handleImageClick = useCallback((deviceId, date, picture) => {
    const pictures = getPicturesForDeviceAndDate(deviceId, date);
    const imageIndex = pictures.findIndex(p => p.key === picture.key);
    
    setModalPictures(pictures);
    setCurrentImageIndex(imageIndex >= 0 ? imageIndex : 0);
    setSelectedImage(picture);
    setImageModalOpen(true);
  }, [pictureCache]);

  // Modal navigation
  const handlePrevImage = useCallback(() => {
    if (modalPictures.length > 1) {
      const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : modalPictures.length - 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(modalPictures[newIndex]);
    }
  }, [modalPictures, currentImageIndex]);

  const handleNextImage = useCallback(() => {
    if (modalPictures.length > 1) {
      const newIndex = currentImageIndex < modalPictures.length - 1 ? currentImageIndex + 1 : 0;
      setCurrentImageIndex(newIndex);
      setSelectedImage(modalPictures[newIndex]);
    }
  }, [modalPictures, currentImageIndex]);

  const handleCloseModal = useCallback(() => {
    setImageModalOpen(false);
    setSelectedImage(null);
    setModalPictures([]);
    setCurrentImageIndex(0);
  }, []);

  // Load debug count status for organization
  const loadDebugCountStatus = useCallback(async () => {
    if (!selectedOrgId) return;

    setLoadingDebugCount(true);
    try {
      console.log(`🔍 Loading debug count status for organization: ${selectedOrgId}`);
      const statusData = await fetchDebugCountStatus(selectedOrgId);
      
      // Convert array to lookup object for faster access
      const statusLookup = {};
      statusData.forEach(status => {
        statusLookup[status.deviceId] = status;
      });
      
      setDebugCountStatus(statusLookup);
      console.log(`✅ Debug count status loaded for ${statusData.length} devices`);
    } catch (error) {
      console.error('❌ Error loading debug count status:', error);
      setDebugCountStatus({});
    } finally {
      setLoadingDebugCount(false);
    }
  }, [selectedOrgId]);

  // Load pictures for all devices on mount
  useEffect(() => {
    reportData.forEach(device => {
      loadDevicePictures(device.deviceId);
    });
  }, [reportData, loadDevicePictures]);

  // Load debug count status when organization changes
  useEffect(() => {
    loadDebugCountStatus();
  }, [loadDebugCountStatus]);

  // Handle visual confirmation request
  const handleRequestVisualConfirmation = useCallback(async () => {
    if (!selectedOrgId) return;

    setRequestingConfirmation(true);
    setShowRequestResult(false);
    
    try {
      console.log(`🔍 Requesting visual confirmation for organization: ${selectedOrgId}`);
      const result = await requestVisualConfirmation(selectedOrgId);
      
      setRequestResult(result);
      setShowRequestResult(true);
      
      // Reload debug count status to show updated values
      loadDebugCountStatus();
      
      console.log(`✅ Visual confirmation requested:`, result);
    } catch (error) {
      console.error('❌ Error requesting visual confirmation:', error);
      setRequestResult({
        message: 'Failed to request visual confirmation',
        error: error.message,
        devicesProcessed: 0,
        devicesUpdated: 0,
        devicesSkipped: 0
      });
      setShowRequestResult(true);
    } finally {
      setRequestingConfirmation(false);
    }
  }, [selectedOrgId, loadDebugCountStatus]);

  if (reportData.length === 0 || dates.length === 0) {
    return (
      <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <WalteroTypography variant="h6" color="secondary">
          No data available for visual review
        </WalteroTypography>
      </WalteroCard>
    );
  }

  return (
    <div className="visual-review-container">
      {/* Request Visual Confirmation Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <WalteroButton
          variant="contained"
          onClick={handleRequestVisualConfirmation}
          disabled={requestingConfirmation || !selectedOrgId}
          startIcon={requestingConfirmation ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: '180px' }}
        >
          {requestingConfirmation ? 'Requesting...' : 'Request Image'}
        </WalteroButton>
        
        <WalteroTypography variant="body2" color="textSecondary" sx={{ maxWidth: '400px' }}>
          Request an image from each sensor in this organization.
        </WalteroTypography>
      </Box>

      {/* Request Result Display */}
      {showRequestResult && requestResult && (
        <Alert 
          severity={requestResult.error ? 'error' : 'success'} 
          onClose={() => setShowRequestResult(false)}
          sx={{ mb: 3 }}
        >
          <Box>
            <WalteroTypography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {requestResult.message}
            </WalteroTypography>
            <WalteroTypography variant="body2">
              Processed: {requestResult.devicesProcessed} devices • 
              Updated: {requestResult.devicesUpdated} • 
              Skipped: {requestResult.devicesSkipped}
              {requestResult.updateErrors && requestResult.updateErrors.length > 0 && 
                ` • Errors: ${requestResult.updateErrors.length}`
              }
            </WalteroTypography>
            {requestResult.error && (
              <WalteroTypography variant="body2" color="error" sx={{ mt: 1 }}>
                Error: {requestResult.error}
              </WalteroTypography>
            )}
          </Box>
        </Alert>
      )}
      
      <div className="visual-review-table-container">
        <table className="visual-review-table">
          <thead>
            <tr>
              <th className="device-header">Device</th>
              <th className="status-header">Pending Uploads</th>
              {dates.map(date => (
                <th key={date} className="date-header">
                  {new Date(date).toLocaleDateString()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportData.sort((a, b) => {
              const serialA = (a.serialNumber || '').toString();
              const serialB = (b.serialNumber || '').toString();
              return serialA.localeCompare(serialB);
            }).map(device => (
              <tr key={device.deviceId}>
                <td className="device-cell">
                  <div className="device-info">
                    <div className="device-serial">{device.serialNumber}</div>
                    <div className="device-name">{device.meterName || 'N/A'}</div>
                  </div>
                </td>
                <td className="status-cell">
                  {(() => {
                    if (loadingDebugCount) {
                      return <div className="status-loading">Loading...</div>;
                    }
                    
                    const deviceStatus = debugCountStatus[device.deviceId];
                    if (!deviceStatus) {
                      return <div className="status-unknown">Unknown</div>;
                    }
                    
                    // Handle different combinations of desired/reported values
                    const desired = deviceStatus.desiredDebugCount;
                    const reported = deviceStatus.reportedDebugCount;
                    
                    // If desired exists but reported is null, or desired > reported = pending
                    if ((desired !== null && reported === null) || deviceStatus.hasPendingImages) {
                      return (
                        <div className="status-pending" title={`Desired: ${desired !== null ? desired : 'null'}, Reported: ${reported !== null ? reported : 'null'}`}>
                          <span className="status-icon">📤</span>
                          <span className="status-text">Pending</span>
                        </div>
                      );
                    } 
                    // If both are null or both are equal = synced
                    else if ((desired === null && reported === null) || (desired !== null && reported !== null && desired === reported)) {
                      return (
                        <div className="status-synced" title={`Desired: ${desired !== null ? desired : 'null'}, Reported: ${reported !== null ? reported : 'null'}`}>
                          <span className="status-icon">✅</span>
                          <span className="status-text">Synced</span>
                        </div>
                      );
                    }
                    // Default fallback
                    else {
                      return (
                        <div className="status-unknown" title={`Desired: ${desired !== null ? desired : 'null'}, Reported: ${reported !== null ? reported : 'null'}`}>
                          <span className="status-icon">❓</span>
                          <span className="status-text">Unknown</span>
                        </div>
                      );
                    }
                  })()}
                </td>
                {dates.map(date => {
                  const pictures = getPicturesForDeviceAndDate(device.deviceId, date);
                  const isLoading = loadingDevices.has(device.deviceId);
                  const hasInference = hasInferenceImagesForDate(device.deviceId, date);
                  
                  return (
                    <td key={date} className="picture-cell">
                      {isLoading ? (
                        <div className="picture-loading">
                          <div className="loading-spinner"></div>
                        </div>
                      ) : pictures.length > 0 ? (
                        <div 
                          className={`picture-container ${hasInference ? 'has-inference' : ''}`}
                          onClick={() => handleImageClick(device.deviceId, date, pictures[0])}
                          title={hasInference ? 'Contains inference images' : undefined}
                        >
                          <img 
                            src={pictures[0].url} 
                            alt={`Device ${device.serialNumber} on ${date}`}
                            className="picture-thumbnail"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="picture-error" style={{ display: 'none' }}>
                            Error
                          </div>
                          {pictures.length > 1 && (
                            <div className="picture-count-badge">
                              {pictures.length}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="no-picture">
                          <span>No Image</span>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Shared Image Modal with Navigation */}
      <ImageModal
        isOpen={imageModalOpen}
        selectedImage={selectedImage}
        pictures={modalPictures}
        currentIndex={currentImageIndex}
        onClose={handleCloseModal}
        onNext={handleNextImage}
        onPrev={handlePrevImage}
        title="Device Image"
      />
    </div>
  );
};

export default VisualReview;